import { useState, useEffect, useRef } from 'react';

export type FundResourceError = (Error & { code?: string | number }) | null;

export interface FundResourceResult<T> {
  data?: T;
  isLoading: boolean;
  error: FundResourceError;
}

const useFundResource = <T>(
  fetcher: (args: {
    token: string;
    ric: string;
    saleInCountryCode?: string;
  }) => Promise<{ data: T } | undefined>,
  ric: string = '',
  token: string = '',
  saleInCountryCode: string = 'US',
): FundResourceResult<T> => {
  const [data, setData] = useState<T>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<FundResourceError>(null);

  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    if (!ric) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    setIsLoading(true);
    setError(null);

    fetcherRef
      .current({ token, ric, saleInCountryCode })
      .then((res) => {
        if (!cancelled) setData(res?.data);
      })
      .catch((err: Error & { code?: string | number }) => {
        if (!cancelled) setError(err);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token, ric, saleInCountryCode]);

  return { data, isLoading, error };
};

export default useFundResource;
